<script setup>
import ReportFiltersSection from './ReportFiltersSection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Table from 'dashboard/components/table/Table.vue';
import { generateStatisticsFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { computed, ref, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import Pagination from 'dashboard/components/table/Pagination.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import SummaryReportLink from './SummaryReportLink.vue';
import SummaryMetricCards from './SummaryMetricCards.vue';
import SummaryReportCharts from './SummaryReportCharts.vue';
import SummaryOverviewHeatmaps from './SummaryOverviewHeatmaps.vue';
import SummaryArchiveLink from './SummaryArchiveLink.vue';
import { useRoute } from 'vue-router';
import { parseSummaryEntityFilterParams } from '../helpers/reportFilterHelper';
import { TIME_CELL_METRIC_MAP } from '../helpers/summaryReportArchiveHelper';
import { formatReportDuration, durationMetricTooltip } from '../constants';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const { uiSettings, updateUISettings } = useUISettings();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedLabelIds = ref([]);
const excludeLabelIds = ref([]);
const selectedInboxIds = ref([]);
const selectedTeamIds = ref([]);
const selectedAgentIds = ref([]);
const selectedChannelTypes = ref([]);
const episodeResult = ref('all');
const receiptType = ref('all');
const tagPresence = ref('all');
const resolutionMethod = ref('all');
const comparisonPeriods = ref([]);
const searchQuery = ref('');
const SUMMARY_TABLE_PAGE_SIZE_KEY = 'summary_reports_table_page_size';
const ALLOWED_PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const applyEntityFilters = filters => {
  selectedAgentIds.value = filters.agentIds || [];
  selectedInboxIds.value = filters.inboxIds || [];
  selectedTeamIds.value = filters.teamIds || [];
  selectedLabelIds.value = filters.labelIds || [];
  excludeLabelIds.value = filters.excludeLabelIds || [];
  selectedChannelTypes.value = filters.channelTypes || [];
  episodeResult.value = filters.episodeResult || 'all';
  receiptType.value = filters.receiptType || 'all';
  tagPresence.value = filters.tagPresence || 'all';
  resolutionMethod.value = filters.resolutionMethod || 'all';
};

applyEntityFilters(parseSummaryEntityFilterParams(route.query));

const allLabels = useMapGetter('labels/getLabels');

const chartFilters = computed(() => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  inboxIds: selectedInboxIds.value,
  teamIds: selectedTeamIds.value,
  labelIds: selectedLabelIds.value,
  excludeLabelIds: excludeLabelIds.value,
  agentIds: selectedAgentIds.value,
  channelTypes: selectedChannelTypes.value,
  episodeResult: episodeResult.value,
  receiptType: receiptType.value,
  tagPresence: tagPresence.value,
  resolutionMethod: resolutionMethod.value,
  comparisonPeriods: comparisonPeriods.value,
}));

const archiveContext = computed(() => {
  const labelTitles = selectedLabelIds.value
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);
  const excludeLabelTitles = excludeLabelIds.value
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);

  return {
    reportType: props.type,
    from: from.value,
    to: to.value,
    entityFilters: {
      agentIds: selectedAgentIds.value,
      inboxIds: selectedInboxIds.value,
      teamIds: selectedTeamIds.value,
      labelIds: selectedLabelIds.value,
      labelTitles,
      excludeLabelIds: excludeLabelIds.value,
      excludeLabelTitles,
      channelTypes: selectedChannelTypes.value,
      episodeResult: episodeResult.value,
      receiptType: receiptType.value,
      tagPresence: tagPresence.value,
      resolutionMethod: resolutionMethod.value,
    },
  };
});

const buildSummaryParams = () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  if (selectedLabelIds.value.length > 0) {
    params.labelIds = selectedLabelIds.value;
  }
  if (excludeLabelIds.value.length > 0) {
    params.excludeLabelIds = excludeLabelIds.value;
  }
  if (selectedInboxIds.value.length > 0) {
    params.inboxIds = selectedInboxIds.value;
  }
  if (selectedTeamIds.value.length > 0) {
    params.teamIds = selectedTeamIds.value;
  }
  if (selectedAgentIds.value.length > 0) {
    params.agentIds = selectedAgentIds.value;
  }
  if (selectedChannelTypes.value.length > 0) {
    params.channelTypes = selectedChannelTypes.value;
  }
  if (episodeResult.value !== 'all') {
    params.episodeResult = episodeResult.value;
  }
  if (receiptType.value !== 'all') {
    params.receiptType = receiptType.value;
  }
  if (tagPresence.value !== 'all') {
    params.tagPresence = tagPresence.value;
  }
  if (resolutionMethod.value !== 'all') {
    params.resolutionMethod = resolutionMethod.value;
  }
  if (comparisonPeriods.value.length) {
    params.comparisonPeriods = comparisonPeriods.value;
  }
  return params;
};

const flagMap = {
  agent: 'isFetchingAgentSummaryReports',
  inbox: 'isFetchingInboxSummaryReports',
  team: 'isFetchingTeamSummaryReports',
  label: 'isFetchingLabelSummaryReports',
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(() => uiFlags.value[flagMap[props.type]] ?? false);

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const comparisonsGetterName = computed(() => {
  const map = {
    inbox: 'summaryReports/getInboxSummaryComparisons',
    agent: 'summaryReports/getAgentSummaryComparisons',
    team: 'summaryReports/getTeamSummaryComparisons',
    label: 'summaryReports/getLabelSummaryComparisons',
  };
  return map[props.type];
});

const comparisonSlices = computed(
  () => store.getters[comparisonsGetterName.value] || []
);

const labelAggregates = useMapGetter(
  'summaryReports/getLabelSummaryAggregates'
);

const ownEntityFilterIds = computed(() => {
  const map = {
    agent: selectedAgentIds.value,
    team: selectedTeamIds.value,
    inbox: selectedInboxIds.value,
    label: selectedLabelIds.value,
  };
  return map[props.type] || [];
});

const hasScopeFilters = computed(
  () =>
    ownEntityFilterIds.value.length > 0 ||
    selectedAgentIds.value.length > 0 ||
    selectedInboxIds.value.length > 0 ||
    selectedTeamIds.value.length > 0 ||
    selectedLabelIds.value.length > 0 ||
    excludeLabelIds.value.length > 0 ||
    selectedChannelTypes.value.length > 0 ||
    episodeResult.value !== 'all' ||
    receiptType.value !== 'all' ||
    tagPresence.value !== 'all' ||
    resolutionMethod.value !== 'all'
);

const hasRowActivity = metrics => {
  if (metrics == null || metrics.id === undefined || metrics.id === null) {
    return false;
  }

  return (
    (metrics.conversationsCount || 0) > 0 ||
    (metrics.uniqueCyclesHandled || 0) > 0 ||
    (metrics.assignmentEpisodesCount || 0) > 0 ||
    (metrics.answeredEpisodesCount || 0) > 0 ||
    (metrics.unansweredEpisodesCount || 0) > 0 ||
    (metrics.resolvedConversationsCount || 0) > 0 ||
    (metrics.transfersInCount || 0) > 0 ||
    (metrics.transfersOutCount || 0) > 0 ||
    (metrics.backlogEnd || 0) > 0 ||
    (metrics.onlineSeconds || 0) > 0 ||
    (metrics.csatResponses || 0) > 0 ||
    metrics.avgFirstResponseTime ||
    metrics.medianFirstResponseTime ||
    metrics.medianResolutionTime ||
    metrics.botResolutionRate != null
  );
};

const withoutTagsCount = computed(
  () => labelAggregates.value?.withoutTagsCount || 0
);

const withoutTagsReport = computed(
  () => labelAggregates.value?.withoutTagsReport || null
);

const withWithoutTagsRow = rows => {
  if (props.type !== 'label' || tagPresence.value === 'with') return rows;
  const report = withoutTagsReport.value;
  if (!report || !(report.conversationsCount > 0)) return rows;
  if (rows.some(row => Number(row.id) === 0)) return rows;

  return [
    ...rows,
    {
      id: 0,
      name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS'),
      title: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS'),
    },
  ];
};

const withoutTeamReport = computed(() => {
  if (props.type !== 'team') return null;
  return reportMetrics.value.find(metrics => Number(metrics.id) === 0) || null;
});

const withWithoutTeamRow = rows => {
  if (props.type !== 'team' || selectedTeamIds.value.length) return rows;
  const report = withoutTeamReport.value;
  if (!report) return rows;
  if (rows.some(row => Number(row.id) === 0)) return rows;

  return [
    ...rows,
    {
      id: 0,
      name: t('SUMMARY_REPORTS.CARDS.TEAM.WITHOUT_TEAM'),
    },
  ];
};

const visibleRowItems = computed(() => {
  let rows;
  if (!hasScopeFilters.value) {
    rows = rowItems.value;
  } else if (ownEntityFilterIds.value.length > 0) {
    const idSet = new Set(ownEntityFilterIds.value.map(Number));
    rows = rowItems.value.filter(row => idSet.has(Number(row.id)));
  } else {
    const itemsById = new Map(rowItems.value.map(row => [Number(row.id), row]));
    rows = reportMetrics.value.filter(hasRowActivity).map(metrics => {
      const entity = itemsById.get(Number(metrics.id)) || {};
      return {
        ...entity,
        id: metrics.id,
        name: metrics.name ?? entity.name ?? entity.title,
        email: entity.email || metrics.email,
        deleted: metrics.deleted || false,
      };
    });
  }

  return withWithoutTeamRow(withWithoutTagsRow(rows));
});

const getMetrics = id => {
  if (Number(id) === 0 && props.type === 'label' && withoutTagsReport.value) {
    return withoutTagsReport.value;
  }
  return (
    reportMetrics.value.find(metrics => Number(metrics.id) === Number(id)) || {}
  );
};

const sliceRowMetrics = (slice, id) =>
  (slice?.data || []).find(m => m.id === Number(id)) || {};

const columnHelper = createColumnHelper();

const renderComparisonLines = (row, accessor, primaryNode) => {
  const comparisonValues = comparisonSlices.value
    .map((_, si) => row[`cmp${si}_${accessor}`])
    .filter(value => value && value !== '--');

  if (!comparisonValues.length) return primaryNode;

  return h('div', { class: 'flex flex-col items-center gap-0.5 min-w-0' }, [
    primaryNode,
    ...comparisonValues.map(value =>
      h(
        'span',
        { class: 'text-[9px] leading-tight text-n-slate-11 tabular-nums' },
        value
      )
    ),
  ]);
};

const displayValue = (cellProps, accessor = cellProps.column.id) =>
  cellProps.row.original[accessor];

const renderTimeCell = cellProps => {
  const value = displayValue(cellProps);
  const seconds = cellProps.row.original[`_sort_${cellProps.column.id}`];
  const definition = cellProps.column.columnDef.meta?.headerTitle;
  const metricKey = TIME_CELL_METRIC_MAP[cellProps.column.id];
  if (!value || value === '--') {
    return renderComparisonLines(
      cellProps.row.original,
      cellProps.column.id,
      h('span', { class: 'text-n-slate-11' }, '--')
    );
  }

  const node = metricKey
    ? h(SummaryArchiveLink, {
        value,
        metricKey,
        row: cellProps.row.original,
        archiveContext: archiveContext.value,
        rawValue: 1,
        title: durationMetricTooltip(t, definition, seconds),
      })
    : h(
        'span',
        {
          class: 'tabular-nums',
          title: durationMetricTooltip(t, definition, seconds),
        },
        value
      );

  return renderComparisonLines(
    cellProps.row.original,
    cellProps.column.id,
    node
  );
};

const renderOnlineTimeCell = cellProps => {
  const row = cellProps.row.original;
  const value = displayValue(cellProps, 'onlineSeconds');
  if (!value || value === '--') {
    return renderComparisonLines(
      row,
      'onlineSeconds',
      h('span', { class: 'text-n-slate-11' }, '--')
    );
  }

  const link = h(
    RouterLink,
    {
      to: {
        name: 'agent_activity_detail',
        params: { id: row.id },
        query: { from: from.value, to: to.value },
      },
      class: 'text-n-brand hover:underline tabular-nums',
    },
    () => value
  );

  return renderComparisonLines(row, 'onlineSeconds', link);
};

const rawMetricKey = accessor => {
  if (accessor === 'resolutionsCount') return 'resolvedConversationsCount';
  if (accessor === 'csat') return 'csatResponses';
  return accessor;
};

const renderArchiveCountCell = accessor => cellProps => {
  const row = cellProps.row.original;
  const metricKey = TIME_CELL_METRIC_MAP[accessor] || accessor;
  const rawMetrics = getMetrics(row.id);

  const link = h(SummaryArchiveLink, {
    value: displayValue(cellProps, accessor),
    metricKey,
    row,
    archiveContext: archiveContext.value,
    rawValue: rawMetrics[rawMetricKey(accessor)],
  });

  return renderComparisonLines(row, accessor, link);
};

const defaulSpanRender = cellProps =>
  renderComparisonLines(
    cellProps.row.original,
    cellProps.column.id,
    h(
      'span',
      {
        class: displayValue(cellProps) ? '' : 'text-n-slate-12',
      },
      displayValue(cellProps) || '--'
    )
  );

const renderCsat = (average, responses) => {
  if (average === null || average === undefined || average === '') return '--';
  const count = responses || 0;
  return `${Number(average).toFixed(1)}/${count}`;
};

const headerLabel = key => t(`SUMMARY_REPORTS.${key}`);
const headerShortLabel = key =>
  t(`SUMMARY_REPORTS.HEADER_SHORT.${key}`, headerLabel(key));

const pushMetricColumn = (
  base,
  {
    accessor,
    headerKey,
    cell = defaulSpanRender,
    groupKey,
    groupLabel,
    sortable = true,
  }
) => {
  const label = headerShortLabel(headerKey);
  const title = headerLabel(headerKey);
  base.push(
    columnHelper.accessor(row => row[`_sort_${accessor}`] ?? 0, {
      id: accessor,
      header: label,
      cell,
      enableSorting: sortable !== false,
      meta: {
        metric: true,
        headerTitle: title,
        groupKey,
        groupLabel,
      },
    })
  );
};

const LINKABLE_ACCESSORS = new Set([
  'conversationsCount',
  'uniqueCyclesHandled',
  'assignmentEpisodesCount',
  'answeredEpisodesCount',
  'unansweredEpisodesCount',
  'transfersInCount',
  'transfersOutCount',
  'resolutionsCount',
  'backlogEnd',
  'csat',
]);

const metricCellRenderer = (accessor, cell = defaulSpanRender) =>
  LINKABLE_ACCESSORS.has(accessor) ? renderArchiveCountCell(accessor) : cell;

const buildColumnAdder = cols => {
  return (
    accessor,
    headerKey,
    cell = defaulSpanRender,
    group = null,
    sortable = true
  ) => {
    cols.push({
      accessor,
      headerKey,
      cell: metricCellRenderer(accessor, cell),
      groupKey: group?.groupKey,
      groupLabel: group?.groupLabel,
      sortable,
    });
  };
};

// TZ §8: performance table (volume / outcomes / transfers).
const agentPerformanceMetricColumns = computed(() => {
  const cols = [];
  const add = buildColumnAdder(cols);
  add('uniqueCyclesHandled', 'UNIQUE_CYCLES');
  add('assignmentEpisodesCount', 'ACCEPTED_EPISODES');
  add('answeredEpisodesCount', 'ANSWERED_EPISODES');
  add('unansweredEpisodesCount', 'UNANSWERED_EPISODES');
  add('resolutionsCount', 'RESOLUTION_COUNT');
  add('transfersInCount', 'TRANSFERS_IN');
  add('transfersOutCount', 'TRANSFERS_OUT');
  return cols;
});

// TZ §8: time & quality table (online FRT, ART, assignment duration, CSAT).
const agentQualityMetricColumns = computed(() => {
  const cols = [];
  const add = buildColumnAdder(cols);
  add('avgFirstResponseTime', 'AVG_FIRST_RESPONSE_TIME', renderTimeCell);
  add('medianFirstResponseTime', 'MEDIAN_FIRST_RESPONSE_TIME', renderTimeCell);
  add('avgOnlineFirstResponseTime', 'AVG_ONLINE_FRT', renderTimeCell);
  add('medianOnlineFirstResponseTime', 'MEDIAN_ONLINE_FRT', renderTimeCell);
  add('avgReplyTime', 'AVG_REPLY_TIME', renderTimeCell);
  add('medianAssignmentDuration', 'MEDIAN_ASSIGNMENT_DURATION', renderTimeCell);
  add('onlineSeconds', 'ONLINE_TIME', renderOnlineTimeCell);
  add('resolvedPerOnlineHour', 'RESOLVED_PER_ONLINE_HOUR');
  add('csat', 'CSAT', null, null, false);
  return cols;
});

const reportMetricColumns = computed(() => {
  if (props.type === 'agent') return agentPerformanceMetricColumns.value;

  const cols = [];
  const throughputGroup =
    props.type === 'team'
      ? {
          groupKey: 'throughput',
          groupLabel: headerLabel('TEAM_THROUGHPUT'),
        }
      : null;
  const add = buildColumnAdder(cols);

  if (props.type === 'inbox') {
    add('channelType', 'CHANNEL_TYPE', defaulSpanRender, null, false);
  }

  if (props.type === 'team') {
    add('uniqueCyclesHandled', 'UNIQUE_CYCLES');
    add(
      'assignmentEpisodesCount',
      'TEAM_RECEIVED',
      defaulSpanRender,
      throughputGroup
    );
    add(
      'resolutionsCount',
      'TEAM_COMPLETED',
      defaulSpanRender,
      throughputGroup
    );
    add('transfersInCount', 'TRANSFERS_IN');
    add('transfersOutCount', 'TRANSFERS_OUT');
  } else {
    add('conversationsCount', 'CONVERSATIONS');
  }

  if (props.type === 'label') {
    add('tagShare', 'TAG_SHARE');
  }

  if (['team', 'inbox', 'label'].includes(props.type)) {
    add('backlogEnd', 'BACKLOG_END');
  }

  add('avgFirstResponseTime', 'AVG_FIRST_RESPONSE_TIME', renderTimeCell);
  add('medianFirstResponseTime', 'MEDIAN_FIRST_RESPONSE_TIME', renderTimeCell);
  add('avgResolutionTime', 'AVG_RESOLUTION_TIME', renderTimeCell);

  if (['inbox', 'label'].includes(props.type)) {
    add('medianResolutionTime', 'MEDIAN_RESOLUTION_TIME', renderTimeCell);
  }

  if (props.type === 'team') {
    add('medianTeamHandlingSeconds', 'MEDIAN_TEAM_HANDLING', renderTimeCell);
    add(
      'medianFullResolutionSeconds',
      'MEDIAN_FULL_RESOLUTION',
      renderTimeCell
    );
  }

  add('avgReplyTime', 'AVG_REPLY_TIME', renderTimeCell);

  if (props.type !== 'team') {
    add('resolutionsCount', 'RESOLUTION_COUNT');
  }

  if (props.type === 'inbox') {
    add('botResolutionRate', 'BOT_RESOLUTION_RATE');
    add('queueAbandonmentRate', 'QUEUE_ABANDONMENT_RATE');
    add('csat', 'CSAT', defaulSpanRender, null, false);
  }

  if (props.type === 'label') {
    add('botResolutionRate', 'BOT_RESOLUTION_RATE');
    add('csat', 'CSAT', defaulSpanRender, null, false);
  }

  if (props.type === 'team') {
    add('csat', 'CSAT', defaulSpanRender, null, false);
  }

  return cols;
});

const buildEntityColumns = metricCols => {
  const entityLabel = headerShortLabel(props.type.toUpperCase());
  const entityTitle = headerLabel(props.type.toUpperCase());
  const base = [
    columnHelper.accessor('name', {
      header: entityLabel,
      cell: cellProps =>
        h(SummaryReportLink, { row: cellProps.row, compact: true }),
      meta: {
        primary: true,
        primaryWide: props.type === 'agent',
        headerTitle: entityTitle,
      },
      sortingFn: 'alphanumeric',
    }),
  ];

  metricCols.forEach(metric => pushMetricColumn(base, metric));
  return base;
};

const columns = computed(() => buildEntityColumns(reportMetricColumns.value));

const qualityColumns = computed(() =>
  props.type === 'agent'
    ? buildEntityColumns(agentQualityMetricColumns.value)
    : []
);

const selectedInboxHeatmapId = computed(() =>
  props.type === 'inbox' && selectedInboxIds.value.length === 1
    ? selectedInboxIds.value[0]
    : null
);

// TZ §8: weekday×hour UTC heatmap when a single operator is selected on the index.
const selectedAgentHeatmapId = computed(() =>
  props.type === 'agent' && selectedAgentIds.value.length === 1
    ? selectedAgentIds.value[0]
    : null
);

const renderAvgTime = value => formatReportDuration(value, t);

const renderCount = value => (value ? value.toLocaleString() : '--');

const renderPercent = value =>
  value === null || value === undefined || value === '' ? '--' : `${value}%`;

const renderRate = value =>
  value === null || value === undefined || value === ''
    ? '--'
    : Number(value).toFixed(1);

const metricCardData = computed(() => {
  if (props.type === 'label' && tagPresence.value === 'without') {
    return reportMetrics.value.map(row => ({
      ...row,
      name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS'),
    }));
  }

  return visibleRowItems.value.map(row => ({
    ...getMetrics(row.id),
    name: row.name ?? row.title,
  }));
});

const buildMetricRow = rowMetrics => {
  const {
    conversationsCount,
    assignmentEpisodesCount,
    uniqueCyclesHandled,
    answeredEpisodesCount,
    unansweredEpisodesCount,
    avgFirstResponseTime,
    medianFirstResponseTime,
    medianOnlineFirstResponseTime,
    avgOnlineFirstResponseTime,
    avgResolutionTime,
    medianResolutionTime,
    medianTeamHandlingSeconds,
    medianFullResolutionSeconds,
    avgReplyTime,
    resolvedConversationsCount,
    resolvedPerOnlineHour,
    tagShare,
    backlogEnd,
    botResolutionRate,
    queueAbandonmentRate,
    onlineSeconds,
    csatAverage,
    csatResponses,
    transfersInCount,
    transfersOutCount,
    medianAssignmentDuration,
    channelType,
  } = rowMetrics;

  const display = {
    conversationsCount: renderCount(conversationsCount),
    assignmentEpisodesCount: renderCount(assignmentEpisodesCount),
    uniqueCyclesHandled: renderCount(uniqueCyclesHandled),
    answeredEpisodesCount: renderCount(answeredEpisodesCount),
    unansweredEpisodesCount: renderCount(unansweredEpisodesCount),
    transfersInCount: renderCount(transfersInCount),
    transfersOutCount: renderCount(transfersOutCount),
    tagShare: renderPercent(tagShare),
    backlogEnd: renderCount(backlogEnd),
    botResolutionRate: renderPercent(botResolutionRate),
    queueAbandonmentRate: renderPercent(queueAbandonmentRate),
    channelType: channelType || '--',
    avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
    medianFirstResponseTime: renderAvgTime(medianFirstResponseTime),
    medianOnlineFirstResponseTime: renderAvgTime(medianOnlineFirstResponseTime),
    avgOnlineFirstResponseTime: renderAvgTime(avgOnlineFirstResponseTime),
    avgReplyTime: renderAvgTime(avgReplyTime),
    avgResolutionTime: renderAvgTime(avgResolutionTime),
    medianResolutionTime: renderAvgTime(medianResolutionTime),
    medianTeamHandlingSeconds: renderAvgTime(medianTeamHandlingSeconds),
    medianFullResolutionSeconds: renderAvgTime(
      medianFullResolutionSeconds || medianResolutionTime
    ),
    resolutionsCount: renderCount(resolvedConversationsCount),
    resolvedPerOnlineHour: renderRate(resolvedPerOnlineHour),
    onlineSeconds: renderAvgTime(onlineSeconds),
    medianAssignmentDuration: renderAvgTime(medianAssignmentDuration),
    csat: renderCsat(csatAverage, csatResponses),
  };

  const sortValues = {
    conversationsCount: conversationsCount || 0,
    assignmentEpisodesCount: assignmentEpisodesCount || 0,
    uniqueCyclesHandled: uniqueCyclesHandled || 0,
    answeredEpisodesCount: answeredEpisodesCount || 0,
    unansweredEpisodesCount: unansweredEpisodesCount || 0,
    transfersInCount: transfersInCount || 0,
    transfersOutCount: transfersOutCount || 0,
    backlogEnd: backlogEnd || 0,
    tagShare: Number(tagShare) || 0,
    botResolutionRate: Number(botResolutionRate) || 0,
    queueAbandonmentRate: Number(queueAbandonmentRate) || 0,
    avgFirstResponseTime: Number(avgFirstResponseTime) || 0,
    medianFirstResponseTime: Number(medianFirstResponseTime) || 0,
    medianOnlineFirstResponseTime: Number(medianOnlineFirstResponseTime) || 0,
    avgOnlineFirstResponseTime: Number(avgOnlineFirstResponseTime) || 0,
    avgResolutionTime: Number(avgResolutionTime) || 0,
    medianResolutionTime: Number(medianResolutionTime) || 0,
    medianTeamHandlingSeconds: Number(medianTeamHandlingSeconds) || 0,
    medianFullResolutionSeconds:
      Number(medianFullResolutionSeconds || medianResolutionTime) || 0,
    avgReplyTime: Number(avgReplyTime) || 0,
    resolutionsCount: resolvedConversationsCount || 0,
    resolvedPerOnlineHour: Number(resolvedPerOnlineHour) || 0,
    onlineSeconds: Number(onlineSeconds) || 0,
    medianAssignmentDuration: Number(medianAssignmentDuration) || 0,
  };

  return {
    ...display,
    ...Object.fromEntries(
      Object.entries(sortValues).map(([key, value]) => [`_sort_${key}`, value])
    ),
  };
};

const tableData = computed(() =>
  visibleRowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    const rowOut = {
      id: row.id,
      name: row.name ?? row.title,
      type: props.type,
      thumbnail: row.thumbnail,
      email: row.email || rowMetrics.email,
      status: row.availability_status,
      deleted: row.deleted || rowMetrics.deleted || false,
      ...buildMetricRow(rowMetrics),
    };

    comparisonSlices.value.forEach((slice, si) => {
      const m = sliceRowMetrics(slice, row.id);
      const cmp = buildMetricRow(m);
      Object.keys(cmp).forEach(key => {
        rowOut[`cmp${si}_${key}`] = cmp[key];
      });
    });

    return rowOut;
  })
);

const searchedTableData = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return tableData.value;

  return tableData.value.filter(row => {
    const haystack = `${row.name || ''} ${row.email || ''}`.toLowerCase();
    return haystack.includes(query);
  });
});

const getPageSize = () => {
  const saved = Number(uiSettings.value[SUMMARY_TABLE_PAGE_SIZE_KEY]);
  return ALLOWED_PAGE_SIZES.includes(saved) ? saved : DEFAULT_PAGE_SIZE;
};

const volumeSortId = computed(() =>
  props.type === 'agent' || props.type === 'team'
    ? 'uniqueCyclesHandled'
    : 'conversationsCount'
);

let lastFetchKey = '';

const fetchReportsWithRetry = async () => {
  const params = buildSummaryParams();
  if (!params.since || !params.until) return;

  const fetchKey = JSON.stringify(params);
  if (fetchKey === lastFetchKey) return;
  lastFetchKey = fetchKey;

  try {
    await store.dispatch(props.actionKey, params);
  } catch {
    try {
      await store.dispatch(props.actionKey, params);
    } catch {
      useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
      lastFetchKey = '';
    }
  }
};

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  fetchReportsWithRetry();
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  businessHours.value = date.businessHours;
  comparisonPeriods.value = date.comparisonPeriods || [];
  applyEntityFilters(entity);
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return searchedTableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: true,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: getPageSize(),
    },
    sorting: [
      {
        id: volumeSortId.value,
        desc: true,
      },
    ],
  },
});

const qualityTable = useVueTable({
  get data() {
    return searchedTableData.value;
  },
  get columns() {
    return qualityColumns.value;
  },
  enableSorting: true,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: getPageSize(),
    },
  },
});

const handlePageSizeChange = pageSize => {
  updateUISettings({ [SUMMARY_TABLE_PAGE_SIZE_KEY]: pageSize });
  table.setPageSize(pageSize);
  if (props.type === 'agent') {
    qualityTable.setPageSize(pageSize);
  }
};

const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateStatisticsFileName({
      type: props.type,
      from: from.value,
      to: to.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
      ...buildSummaryParams(),
    };
    delete params.since;
    delete params.until;
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFiltersSection
    :type="type"
    :disabled="isLoading"
    @filters-apply="onFiltersApply"
  />
  <SummaryMetricCards
    :type="type"
    :metrics="metricCardData"
    :tag-presence="tagPresence"
    :without-tags-count="type === 'label' ? withoutTagsCount : 0"
    :archive-context="archiveContext"
    :primary-from="from"
    :primary-to="to"
    :comparison-slices="comparisonSlices"
  />
  <SummaryReportCharts
    :type="type"
    :filters="chartFilters"
    :archive-context="archiveContext"
  />
  <!-- TZ §13: source heatmap (weekday×hour UTC) when a single inbox is selected. -->
  <SummaryOverviewHeatmaps
    v-if="selectedInboxHeatmapId"
    entity-type="inbox"
    :entity-id="selectedInboxHeatmapId"
    :filters="chartFilters"
    :archive-context="archiveContext"
  />
  <!-- TZ §8: operator heatmap (weekday×hour UTC) when a single agent is selected. -->
  <SummaryOverviewHeatmaps
    v-if="selectedAgentHeatmapId"
    entity-type="agent"
    :entity-id="selectedAgentHeatmapId"
    :filters="chartFilters"
    :archive-context="archiveContext"
  />
  <div
    class="relative flex-1 px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div class="mb-3 flex flex-wrap items-center justify-between gap-2 px-2">
      <h3 v-if="type === 'agent'" class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.TABLES.AGENT_PERFORMANCE') }}
      </h3>
      <input
        v-model="searchQuery"
        type="search"
        class="w-full max-w-xs rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        :placeholder="$t('SUMMARY_REPORTS.TABLE_SEARCH')"
      />
    </div>
    <Table :table="table" type="summary" />
    <Pagination
      class="px-2 pb-2"
      :table="table"
      show-page-size-selector
      :default-page-size="getPageSize()"
      @page-size-change="handlePageSizeChange"
    />
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 flex justify-center pt-[12.5rem] bg-n-solid-1/70 rounded-xl pointer-events-none"
      >
        <Spinner :size="32" class="text-n-brand" />
      </div>
    </Transition>
  </div>
  <div
    v-if="type === 'agent'"
    class="relative flex-1 px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="mb-3 px-2 text-base font-medium text-n-slate-12">
      {{ $t('SUMMARY_REPORTS.TABLES.AGENT_TIME_QUALITY') }}
    </h3>
    <Table :table="qualityTable" type="summary" />
    <Pagination
      class="px-2 pb-2"
      :table="qualityTable"
      show-page-size-selector
      :default-page-size="getPageSize()"
      @page-size-change="handlePageSizeChange"
    />
  </div>
</template>
